import { useController, UseFormReturn } from "react-hook-form";

import { isRequired, isSelectionRequired } from "../../../../helpers/validators";
import { useTranslate } from "../../../../hooks/useTranslate";
import { TicketingPaymentInput } from "../../../../types/ticketing";
import { DonationPaymentInput } from "../../context/FormV2Context";

import { GiftAidBlockLayout } from "./GiftAidBlockLayout";
import { GiftAidCertifiedAlert } from "./GiftAidCertifiedAlert";
import { GiftAidCheckbox } from "./GiftAidCheckbox";
import { GiftAidDescription } from "./GiftAidDescription";
import { GiftAidDetails } from "./GiftAidDetails";
import { GiftAidRadio } from "./GiftAidRadio";

export type GiftAidBlockState = "error" | "selected" | "active";

export interface GiftAidBlockProps {
  form: UseFormReturn<DonationPaymentInput | TicketingPaymentInput>;
  amount: number;
  organizationName: string;
}

export const GiftAidBlock = ({ form, amount, organizationName }: GiftAidBlockProps) => {
  const { t } = useTranslate();
  const { field: addGiftAidField, formState: addGiftAidFormState } = useController({
    control: form.control,
    name: "addGiftAid",
    rules: isSelectionRequired(t("dashboard", "addGiftAid.error")),
  });
  const { field: hasGiftAidConfirmationField, formState: hasGiftAidConfirmationFormState } = useController({
    control: form.control,
    name: "hasGiftAidConfirmation",
    rules: isRequired(Boolean(addGiftAidField?.value), t("dashboard", "hasGiftAidConfirmation.error")),
  });

  const addGiftAidError = addGiftAidFormState?.errors?.addGiftAid?.message as string;
  const hasGiftAidConfirmationError = hasGiftAidConfirmationFormState?.errors?.hasGiftAidConfirmation
    ?.message as string;
  const isError = Boolean(addGiftAidError || hasGiftAidConfirmationError);

  const handleChangeRadio = (value: boolean) => {
    form.trigger("addGiftAid", { shouldFocus: true });

    if (!value) {
      // Reset hasGiftAidConfirmation to false when Gift Aid is declined
      form.setValue("hasGiftAidConfirmation", false);

      // This is a workaround to fix the issue with the checkbox not being validated when the radio button is changed
      setTimeout(() => {
        form.trigger("hasGiftAidConfirmation", { shouldFocus: true });
      }, 10);
    }
  };

  const handleChangeCheckbox = () => {
    form.trigger("hasGiftAidConfirmation", { shouldFocus: true });
  };

  return (
    <GiftAidBlockLayout
      isOpen={addGiftAidField.value === true}
      isAlertOpen={hasGiftAidConfirmationField.value === true}
      error={isError}
      slots={{
        description: <GiftAidDescription amount={amount} />,
        radio: <GiftAidRadio field={addGiftAidField} errorMessage={addGiftAidError} onChange={handleChangeRadio} />,
        details: <GiftAidDetails />,
        checkbox: (
          <GiftAidCheckbox
            field={hasGiftAidConfirmationField}
            errorMessage={hasGiftAidConfirmationError}
            onChange={handleChangeCheckbox}
          />
        ),
        alert: <GiftAidCertifiedAlert amount={amount} organizationName={organizationName} />,
      }}
    />
  );
};
